import React, { createContext, useContext, useState, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { Estate, User } from '../types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken?: string | null;
}

interface GlobalContextType {
  auth: AuthState;
  setAuth: React.Dispatch<React.SetStateAction<AuthState>>;
  selectedEstate: Estate | null;
  setSelectedEstate: React.Dispatch<React.SetStateAction<Estate | null>>;
  /** Make an estate the active one, in state and in the cookie, in one call. */
  selectEstate: (estate: Estate) => void;
  clearSelectedEstate: () => void;
  isAuthenticated: boolean;
  loading: boolean; // Loading state to wait until cookies are read
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialAuth = (): AuthState => {
    try {
      const storedUser = Cookies.get('user');
      const storedToken = Cookies.get('accessToken');
      const storedRefreshToken = Cookies.get('refreshToken');
      if (storedUser && storedToken) {
        return {
          user: JSON.parse(storedUser),
          accessToken: storedToken,
          refreshToken: storedRefreshToken || undefined,
        };
      }
    } catch (error) {
      console.error('Failed to parse auth cookies:', error);
      // Optionally, clear invalid cookies
      Cookies.remove('user');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
    }
    return { user: null, accessToken: null };
  };

  const getInitialEstate = (): Estate | null => {
    try {
      const storedEstate = Cookies.get('selectedEstate');
      if (!storedEstate) return null;
      const estate = JSON.parse(storedEstate);
      // Self-heal cookies written by older versions that stored the full estate
      // object (with all relations). Oversized cookies bloat every request's
      // headers until the server rejects them, so shrink to the minimal shape.
      if (storedEstate.length > 500) {
        const minimalEstate = { uuid: estate.uuid, estateName: estate.estateName };
        Cookies.set('selectedEstate', JSON.stringify(minimalEstate), { expires: 7 });
        return minimalEstate as Estate;
      }
      return estate;
    } catch (error) {
      console.error('Failed to parse selectedEstate cookie:', error);
      Cookies.remove('selectedEstate');
      return null;
    }
  };

  const [auth, setAuth] = useState<AuthState>(getInitialAuth);
  const [selectedEstate, setSelectedEstate] = useState<Estate | null>(getInitialEstate);
  const [loading, setLoading] = useState(false); // No longer needed since initialization is synchronous

  const isAuthenticated = !!auth.user && !!auth.accessToken;

  // Persist only what consumers need. Storing the full estate object makes the
  // cookie huge, and cookies are sent with every request: oversized headers
  // eventually get requests (including login) rejected by the server.
  const selectEstate = (estate: Estate) => {
    setSelectedEstate(estate);
    const minimalEstate = { uuid: estate.uuid, estateName: estate.estateName };
    Cookies.set('selectedEstate', JSON.stringify(minimalEstate), { expires: 7 });
  };

  const clearSelectedEstate = () => {
    setSelectedEstate(null);
    Cookies.remove('selectedEstate');
  };

  return (
    <GlobalContext.Provider
      value={{
        auth,
        setAuth,
        selectedEstate,
        setSelectedEstate,
        selectEstate,
        clearSelectedEstate,
        isAuthenticated,
        loading: false, // Since initialization is synchronous
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
